/**
 * FAQ Data for SEO Structured Data
 * Used across multiple pages for FAQ schema markup
 */

// General Fencing FAQs
export const generalFencingFAQs = [
  {
    question: "How much does a fence cost in Central Alberta?",
    answer: "Fence costs vary by material and height. Chain link starts around $15-25/linear foot, wood fencing $25-45/linear foot, and vinyl fencing $35-65/linear foot. Contact us for a free, accurate quote based on your specific requirements."
  },
  {
    question: "Do I need a permit to build a fence in Red Deer?",
    answer: "Most municipalities in Central Alberta require permits for fences over 6 feet tall or in certain zones. We provide a comprehensive bylaws guide for Red Deer, Lacombe, Ponoka, and surrounding areas. Always check your local bylaws before starting."
  },
  {
    question: "How long does fence installation take?",
    answer: "A typical residential fence (100-200 linear feet) takes 1-3 days to install. Commercial projects vary based on size. Materials are usually in stock for immediate pickup or delivery within 1-2 business days."
  },
  {
    question: "Do you deliver fence materials?",
    answer: "Yes! We offer local delivery throughout Central Alberta including Red Deer, Lacombe, Ponoka, Innisfail, Sylvan Lake, and surrounding areas. Delivery fees depend on distance and order size."
  }
];

// Vinyl Fence FAQs
export const vinylFenceFAQs = [
  {
    question: "What is the warranty on vinyl fencing?",
    answer: "Our VEKA vinyl fencing comes with a Lifetime Limited Warranty covering defects in materials, yellowing, peeling, and structural integrity. Catalyst Newbury vinyl also includes a Limited Lifetime Warranty. Both warranties are transferable to new homeowners."
  },
  {
    question: "Does vinyl fencing turn yellow over time?",
    answer: "Quality vinyl fencing from VEKA and Catalyst is formulated with UV inhibitors to prevent yellowing. Our products are backed by warranties against yellowing, giving you peace of mind for decades."
  },
  {
    question: "Can vinyl fencing handle Alberta winters?",
    answer: "Absolutely. Our VEKA and Catalyst vinyl products are specifically rated for extreme temperatures from -40°C to +50°C. The material remains flexible in cold weather to prevent cracking."
  },
  {
    question: "What colors are available for vinyl fencing?",
    answer: "We stock white, almond, khaki, and gray vinyl. Premium woodgrain finishes like Coastal Cedar, Driftwood, and Walnut are available on special order with 2-8 week lead times depending on the product line."
  },
  {
    question: "Is vinyl fencing more expensive than wood?",
    answer: "While vinyl has a higher upfront cost, it requires zero maintenance (no painting, staining, or sealing) and lasts 30+ years. Over time, vinyl fencing typically costs less than wood when you factor in maintenance."
  }
];

// Wood Fence FAQs
export const woodFenceFAQs = [
  {
    question: "What type of wood do you use for fencing?",
    answer: "We use MicroPro pressure-treated lumber from Rona, which uses copper azole treatment for superior rot and insect resistance. We also offer cedar options for a natural, premium look."
  },
  {
    question: "How long does a wood fence last in Alberta?",
    answer: "With proper maintenance, pressure-treated wood fences last 15-25 years in Alberta's climate. Cedar fences can last 20-30+ years. Annual sealing and occasional staining extend lifespan significantly."
  },
  {
    question: "Do wood fences need to be sealed?",
    answer: "Yes, we recommend sealing wood fences within the first year and every 2-3 years thereafter. This protects against moisture, UV damage, and Alberta's freeze-thaw cycles."
  },
  {
    question: "Can I paint or stain my wood fence?",
    answer: "Yes! Wood fences can be painted or stained in any color. We recommend letting new pressure-treated wood dry for 2-3 months before applying finish for best adhesion."
  }
];

// Chain Link Fence FAQs
export const chainLinkFenceFAQs = [
  {
    question: "Is galvanized chain link better than vinyl-coated?",
    answer: "Galvanized chain link is more economical and equally durable. Vinyl-coated (black, green, or brown) offers a softer appearance and blends better with landscaping. Both options have similar lifespans of 20+ years."
  },
  {
    question: "What heights are available for chain link fencing?",
    answer: "We stock chain link in heights from 3 feet to 12 feet. Residential typically uses 4-6 foot heights, while commercial and industrial applications often use 6-8 foot with optional barbed wire or razor ribbon top."
  },
  {
    question: "Can you add privacy to chain link fencing?",
    answer: "Yes! We offer privacy slats in various colors that weave into the chain link mesh, providing 70-90% privacy. We also offer privacy mesh screens for temporary or permanent coverage."
  },
  {
    question: "How much does chain link fencing cost?",
    answer: "Chain link is the most economical fencing option, starting around $15-25 per linear foot installed. Material-only prices are even lower for DIY installations."
  }
];

// Ornamental Fence FAQs
export const ornamentalFenceFAQs = [
  {
    question: "What is the difference between ornamental iron and aluminum fencing?",
    answer: "Aluminum is lighter, rust-proof, and maintenance-free. Wrought iron is heavier and stronger but requires periodic painting to prevent rust. We carry both options from top manufacturers like Ameristar."
  },
  {
    question: "Can ornamental fencing be used for pools?",
    answer: "Yes, we carry pool-code compliant ornamental fencing that meets Alberta building codes. These feature self-closing, self-latching gates and proper picket spacing for child safety."
  },
  {
    question: "How long does the powder coating last?",
    answer: "Quality powder coating on aluminum fencing typically lasts 15-20+ years without peeling or fading. Our Ameristar products feature a multi-stage powder coat process with warranty protection."
  }
];

// Warranty FAQs
export const warrantyFAQs = [
  {
    question: "How do I make a warranty claim?",
    answer: "Contact Central Alberta Fencing with your proof of purchase and photos of the issue. We handle the warranty claim process directly with manufacturers on your behalf, making it hassle-free."
  },
  {
    question: "Are fence warranties transferable to new owners?",
    answer: "Most of our vinyl fence warranties (VEKA and Catalyst) are fully transferable to new homeowners, adding value to your property when you sell."
  },
  {
    question: "What is not covered under fence warranties?",
    answer: "Warranties typically exclude damage from improper installation, acts of nature (extreme wind, hail), misuse, and normal weathering. Commercial applications may have separate warranty terms."
  }
];

// Property Services FAQs
export const propertyServicesFAQs = [
  {
    question: "Do you offer commercial snow removal contracts?",
    answer: "Yes, we offer seasonal snow removal contracts for commercial properties in Central Alberta. Our contracts include 24/7 emergency response, de-icing, and documented service reports."
  },
  {
    question: "What areas do you serve for property services?",
    answer: "CAF Property Services covers Red Deer and surrounding communities within approximately 80km, including Lacombe, Ponoka, Innisfail, Sylvan Lake, Blackfalds, and more."
  },
  {
    question: "Do you provide landscaping maintenance?",
    answer: "Yes, we offer complete commercial landscaping maintenance including lawn care, irrigation management, seasonal cleanup, and garden maintenance on weekly or bi-weekly schedules."
  }
];

// Calculator/Pricing FAQs
export const calculatorFAQs = [
  {
    question: "How accurate is the online fence calculator?",
    answer: "Our calculator provides estimates based on standard configurations. Actual pricing may vary based on terrain, access, removal of old fencing, and specific requirements. We recommend a free on-site quote for accuracy."
  },
  {
    question: "Do you offer contractor pricing?",
    answer: "Yes! We offer volume discounts and trade accounts for contractors and builders. Apply for a trade account on our Contractors page or contact us directly for pricing."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept cash, debit, Visa, Mastercard, e-transfer, and offer financing options through our partners. Commercial accounts can apply for net-30 terms."
  }
];

// Contractor FAQs
export const contractorFAQs = [
  {
    question: "How do I become an approved contractor?",
    answer: "Apply through our Contractors page with your business information and WCB coverage. Approved contractors receive volume pricing, priority scheduling, and marketing support."
  },
  {
    question: "Do you supply materials only, or installation too?",
    answer: "Both! We're primarily a materials supplier, but we also have trusted installation partners for full-service projects. Many contractors purchase materials from us for their own installations."
  },
  {
    question: "What brands do you carry?",
    answer: "We're authorized dealers for VEKA, Catalyst Newbury (vinyl), Direct Fencing Supply, Ameristar (ornamental), and Rona (wood). We carry 105+ products across all fencing categories."
  }
];
